import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Page } from '@shared/ui/containers/Page';
import { Button } from '@shared/ui/primitives/Button';
import { completeTokenization } from './billingApi';

type Status = 'processing' | 'success' | 'error';

export function TokenizationCallback(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<Status>('processing');
  const [message, setMessage] = useState('Processing card tokenization...');

  const gatewayMetadata = useMemo(() => Object.fromEntries(params.entries()), [params]);

  useEffect(() => {
    const paymentLinkId = params.get('payment_link_id') ?? params.get('PaymentLinkId') ?? '';
    const token = params.get('Token') ?? '';
    const maskedNumber = params.get('MaskedNumber') ?? '';
    const errorMessage = params.get('Error') ?? params.get('error') ?? '';

    if (!paymentLinkId) {
      setStatus('error');
      setMessage('Missing tokenization reference. Please retry adding your card.');
      return;
    }
    if (errorMessage) {
      setStatus('error');
      setMessage(errorMessage);
      return;
    }
    if (!token || !maskedNumber) {
      setStatus('error');
      setMessage('Tokenization details were not provided. Please retry adding your card.');
      return;
    }

    let cancelled = false;
    completeTokenization({
      payment_link_id: paymentLinkId,
      token,
      masked_number: maskedNumber,
      cardholder: params.get('CardNme') ?? params.get('CardName') ?? undefined,
      expiry_month: params.get('ExpiryMonth') ?? undefined,
      expiry_year: params.get('ExpiryYear') ?? undefined,
      gateway_metadata: gatewayMetadata,
    })
      .then(() => {
        if (cancelled) return;
        setStatus('success');
        setMessage('Card saved successfully. Redirecting to your profile...');
        window.setTimeout(() => navigate('/app/profile', { replace: true }), 1600);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Failed to save tokenized card.');
      });

    return () => {
      cancelled = true;
    };
  }, [gatewayMetadata, navigate, params]);

  return (
    <Page scroll={false}>
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
        {status === 'processing' ? (
          <Loader2 className="h-10 w-10 animate-spin text-primary-600" strokeWidth={1.5} />
        ) : null}
        {status === 'success' ? (
          <CheckCircle2 className="h-10 w-10 text-success" strokeWidth={1.5} />
        ) : null}
        {status === 'error' ? (
          <AlertCircle className="h-10 w-10 text-danger" strokeWidth={1.5} />
        ) : null}
        <h1 className="font-display text-display-xl text-ink">
          {status === 'success' ? 'Card added.' : status === 'error' ? "Card wasn't saved." : 'Saving your card...'}
        </h1>
        <p className="max-w-sm text-body text-ink-secondary">{message}</p>
        {status === 'error' ? (
          <Button onClick={() => navigate('/app/profile', { replace: true })}>Back to profile</Button>
        ) : null}
      </div>
    </Page>
  );
}
